import type { Command } from "../document/commands.ts";
import type { TilesetFrames } from "../document/tileset-change.ts";
import { framesWithout } from "../document/tileset-frames.ts";
import { dropStep, firstUses, pickOverflow, placementsFor, pullStep, stepsCommand, type PickStepOf } from "../document/tileset-picks.ts";
import type { WorldDocument } from "../document/types.ts";
import type { Plan } from "./undo-plan.ts";

/**
 * How the session's undo takes a tileset change back (ADR-086 decision 27, ADR-129 decision 3.8), one tileset
 * at a time against the document as it is now. An appended whole entry is removed where the world still lists
 * it whole, and a removed one appended when the world no longer lists it. A pull is taken back by a drop of the
 * tiles it added that a partial entry still holds, and a drop by a pull of the tiles it took out. An entry an
 * Add made whole stays whole: too much art offered, nothing lost. Each plan's command is one the stream
 * carries, so it resolves on every replica as it applies here.
 */

/**
 * The plan for a part that cannot go back because someone changed what it left.
 */
const LOST: Plan = { tries: [], keys: 0, lost: 1 };

/**
 * A plan of one command over one tileset.
 *
 * @param world The document as it is now.
 * @param step The tileset and what its part of the undo does.
 * @returns The plan, lost when the step changes nothing.
 */
function planOf(world: WorldDocument, step: PickStepOf): Plan {
  const command = stepsCommand(world, [step]);
  return command === null ? LOST : { tries: [command], keys: 1, lost: 0 };
}

/**
 * How to take back one whole tileset of a tileset change: an inserted one is removed where the world still
 * lists it whole, a removed one appended when the world no longer lists it. A tileset still in use stays,
 * since the document refuses its removal.
 *
 * @param world The document as it is now.
 * @param id The tileset.
 * @param inserted Whether the edit inserted it.
 * @returns The plan.
 */
function wholePlan(world: WorldDocument, id: string, inserted: boolean): Plan {
  const held = world.tilesets.find((tileset) => tileset.id === id);
  if (inserted) {
    return held === undefined || held.frames !== undefined ? LOST : planOf(world, [id, { kind: "remove" }]);
  }
  return held === undefined ? planOf(world, [id, { kind: "insert" }]) : LOST;
}

/**
 * How to take back tiles a pull added: a drop of those a partial entry still holds, which leaves the ones
 * something places and removes the entry with its last frame when nothing names the tileset.
 *
 * @param world The document as it is now.
 * @param id The tileset.
 * @param tiles The tiles the pull added.
 * @returns The plan; lost when the entry is gone or whole, holds none of them, or something placed stops it.
 */
function pulledPlan(world: WorldDocument, id: string, tiles: readonly number[]): Plan {
  const placed = placementsFor(world, [id]);
  return planOf(world, [id, dropStep(world.tilesets.find((tileset) => tileset.id === id), tiles, () => placed(id), firstUses(world))]);
}

/**
 * How to take back tiles a drop took out: a pull of them, into the entry the world holds or as an entry
 * appended at the end.
 *
 * @param world The document as it is now.
 * @param id The tileset.
 * @param tiles The tiles the drop took out.
 * @returns The plan; lost when the world lists the tileset whole or already holds them, or when the pull would
 *   pass a pick cap, as its operation would be `limit`.
 */
function droppedPlan(world: WorldDocument, id: string, tiles: readonly number[]): Plan {
  const step: PickStepOf = [id, pullStep(world.tilesets.find((tileset) => tileset.id === id), tiles)];
  return pickOverflow(world.tilesets, [step], new Set([id])) === null ? planOf(world, step) : LOST;
}

/**
 * The planners of one entry whose frames a change set in place.
 *
 * @param change The entry's frames before and after.
 * @returns A planner for the tiles the change added and one for the tiles it took out; for an entry made
 *   whole, or a whole entry given frames, one that leaves it as it is.
 */
function framesPlanners(change: TilesetFrames): ((world: WorldDocument) => Plan)[] {
  const { id, before, after } = change;
  if (before === undefined || after === undefined) {
    return [() => LOST];
  }
  const came = framesWithout(after, before);
  const went = framesWithout(before, after);
  return [...(came.length === 0 ? [] : [(world: WorldDocument) => pulledPlan(world, id, came)]), ...(went.length === 0 ? [] : [(world: WorldDocument) => droppedPlan(world, id, went)])];
}

/**
 * How each tileset of a tileset change may be taken back, each planned against the document the ones before
 * it left: the inserted entries, then the entries whose frames were set, then the removed ones.
 *
 * @param command The command as it applied.
 * @returns One planner a part.
 */
export function tilesetPlanners(command: Extract<Command, { readonly kind: "changeTilesets" }>): readonly ((world: WorldDocument) => Plan)[] {
  return [
    ...command.insert.map((entry) => (world: WorldDocument) => (entry.frames === undefined ? wholePlan(world, entry.id, true) : pulledPlan(world, entry.id, entry.frames))),
    ...(command.frames ?? []).flatMap(framesPlanners),
    ...command.remove.map((entry) => (world: WorldDocument) => (entry.frames === undefined ? wholePlan(world, entry.id, false) : droppedPlan(world, entry.id, entry.frames))),
  ];
}
